const STORAGE_KEY = 'pixlide.external-auth';

/** Fournisseurs que l'interface sait nommer et dessiner. */
export const PROVIDER_LABELS: Record<string, string> = {
  linkedin: 'LinkedIn',
  google: 'Google',
};

export function providerLabel(provider: string): string {
  return PROVIDER_LABELS[provider] ?? provider;
}

/**
 * Ce que l'utilisateur était en train de faire avant de partir chez le fournisseur.
 * `login` ouvre une session, `link` associe le fournisseur au compte déjà connecté.
 */
export type ExternalAuthIntent = 'login' | 'link';

export interface PendingExternalAuth {
  intent: ExternalAuthIntent;
  provider: string;
}

/**
 * Mémorise l'intention le temps de l'aller-retour chez le fournisseur.
 *
 * Nécessaire parce que l'URI de retour est FIXE : le fournisseur exige qu'elle soit
 * déclarée à l'avance et identique à chaque appel, elle ne peut donc pas porter le
 * contexte. `sessionStorage` plutôt que `localStorage` : la donnée n'a de sens que dans
 * l'onglet qui a lancé la demande, et disparaît avec lui.
 *
 * Ce n'est PAS ce qui sécurise le parcours. L'intention fait AUSSI foi côté serveur, dans
 * le cookie `HttpOnly` déposé au départ : la modifier ici ne permet donc pas de passer une
 * demande de connexion pour une demande d'association, qui elle exige le mot de passe.
 */
export function rememberPendingExternalAuth(pending: PendingExternalAuth): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
  } catch {
    // Sans stockage, l'écran de retour affichera son message « demande introuvable » et
    // proposera de recommencer — dégradé, mais jamais bloquant.
  }
}

/** Lit et CONSOMME la demande en cours : elle ne doit servir qu'une fois. */
export function consumePendingExternalAuth(): PendingExternalAuth | null {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    window.sessionStorage.removeItem(STORAGE_KEY);

    if (stored === null) {
      return null;
    }

    const parsed = JSON.parse(stored) as PendingExternalAuth;

    return parsed.intent === 'login' || parsed.intent === 'link' ? parsed : null;
  } catch {
    return null;
  }
}
